import { useQuery } from '@tanstack/react-query';

import ledgerViewsApi from '../../../api/ledgerViewsApi';
import { LedgerDatasetFilterParams, LedgerViewMode } from '../../../api/types';

interface UseLedgerDatasetQueriesParams {
    activeDatasetSource: LedgerDatasetFilterParams['source'];
    datasetFilters: Omit<LedgerDatasetFilterParams, 'source'>;
    ledgerViewMode: LedgerViewMode;
    canFetchLedgerData: boolean;
    isGlCodeView: boolean;
    inventoryQueryReady: boolean;
    selectedBusinessId?: string;
}

const useLedgerDatasetQueries = ({
    activeDatasetSource,
    datasetFilters,
    ledgerViewMode,
    canFetchLedgerData,
    isGlCodeView,
    inventoryQueryReady,
    selectedBusinessId,
}: UseLedgerDatasetQueriesParams) => {
    const { data: datasetData, isLoading: isDatasetLoading, refetch: refetchDatasetData, isFetching: isDatasetFetching } = useQuery({
        queryKey: ['ledger-dataset', activeDatasetSource, selectedBusinessId, datasetFilters],
        queryFn: () => ledgerViewsApi.getLedgerItems({
            ...datasetFilters,
            business_id: datasetFilters.business_id || selectedBusinessId,
            source: activeDatasetSource,
        }),
        enabled: ledgerViewMode !== 'cash' && !isGlCodeView && canFetchLedgerData && inventoryQueryReady,
    });

    const { data: datasetFilterOptions } = useQuery({
        queryKey: ['ledger-filter-options', activeDatasetSource, selectedBusinessId],
        queryFn: () => ledgerViewsApi.getFilterOptions({
            source: activeDatasetSource === 'inventory'
                ? 'inventory'
                : activeDatasetSource === 'ar_customers' || activeDatasetSource === 'ar_ledger'
                    ? activeDatasetSource
                    : activeDatasetSource === 'ap_vendors'
                        ? 'ap_vendors'
                        : activeDatasetSource === 'ap_ledger'
                            ? 'ap_ledger'
                            : activeDatasetSource === 'general_ledger'
                                ? 'general_ledger'
                                : 'transaction_list',
            business_id: selectedBusinessId || undefined,
        }),
        enabled: canFetchLedgerData && !isGlCodeView && (
            activeDatasetSource === 'transaction_list'
            || activeDatasetSource === 'general_ledger'
            || activeDatasetSource === 'ar_customers'
            || activeDatasetSource === 'ar_ledger'
            || activeDatasetSource === 'ap_vendors'
            || activeDatasetSource === 'ap_ledger'
            || activeDatasetSource === 'inventory'
        ),
    });

    return {
        datasetData,
        isDatasetLoading,
        refetchDatasetData,
        isDatasetFetching,
        datasetFilterOptions,
    };
};

export default useLedgerDatasetQueries;
